/**
 * Presentation rules for the Budget screen (f10.10). Nothing here computes a
 * budget figure: every amount, total and unallocated value is the server's
 * decimal STRING, rendered verbatim -- only grouped with thousands separators
 * for reading, and never parsed to a float.
 */

import { ApiError, type BudgetCurrent, type ClientAnnualBudgetItem } from '@/api/client';

export { isParsing, refusalMessage, severityPill } from '../cycles/steps/usEngagements/usEngagementsView';

export const MONTH_LABELS: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** A small window around the current calendar year: budget is set ahead and corrected behind. */
export function yearOptions(current: number): number[] {
  return [current - 2, current - 1, current, current + 1, current + 2];
}

/** "11023535.89" -> "11,023,535.89". String work only -- a float would lose cents. */
export function groupDigits(amount: string | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '';
  const negative = amount.startsWith('-');
  const [whole, fraction] = (negative ? amount.slice(1) : amount).split('.');
  const grouped = (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '−' : ''}${grouped}${fraction !== undefined ? `.${fraction}` : ''}`;
}

/** What the amount field accepts before the server has its say: digits, one point, two places. */
export function looksLikeAmount(text: string): boolean {
  return /^\d{1,16}(\.\d{1,2})?$/.test(text.replace(/,/g, '').trim());
}

/** The typed text as sent: separators removed, never converted to a number. */
export function normaliseAmount(text: string): string {
  return text.replace(/,/g, '').trim();
}

/** Sign of a server decimal string, for the unallocated badge. */
export function signOf(amount: string): 'positive' | 'negative' | 'zero' {
  if (/^-?0(\.0*)?$/.test(amount)) return 'zero';
  return amount.startsWith('-') ? 'negative' : 'positive';
}

export function unallocatedPill(amount: string): string {
  const sign = signOf(amount);
  if (sign === 'negative') return 'badge pill-danger';
  if (sign === 'positive') return 'badge pill-warning';
  return 'badge pill-success';
}

export interface UnitGroup {
  unitId: string;
  unitName: string;
  reportingCurrency: string;
  unitActive: boolean;
  items: ClientAnnualBudgetItem[];
}

/** Clients grouped by Business Unit, in the server's order (unit name, then client name). */
export function groupByUnit(items: readonly ClientAnnualBudgetItem[]): UnitGroup[] {
  const groups: UnitGroup[] = [];
  for (const item of items) {
    let group = groups.find((g) => g.unitId === item.business_unit.id);
    if (!group) {
      group = {
        unitId: item.business_unit.id,
        unitName: item.business_unit.name,
        reportingCurrency: item.business_unit.reporting_currency_code,
        unitActive: item.business_unit.is_active,
        items: [],
      };
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

/** How many clients of a group carry a figure this year. */
export function budgetedCount(group: UnitGroup): number {
  return group.items.filter((item) => item.amount !== null).length;
}

/** The server's current figure on a lost update, or null for any other failure. */
export function conflictCurrent(error: unknown): BudgetCurrent | null {
  return error instanceof ApiError && error.reason === 'VERSION_CONFLICT' ? error.current : null;
}

/** The field-bound message for a key (`amount`, `currency_code`, `months.3.amount`), if any. */
export function fieldError(error: unknown, key: string): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.errors.find((item) => item.key === key)?.message ?? null;
}

const ACTION_PILLS: Readonly<Record<string, string>> = {
  CREATE: 'pill-success',
  UPDATE: 'pill-info',
  UNCHANGED: 'pill-neutral',
  INVALID: 'pill-danger',
};

export function actionPill(code: string): string {
  return `badge ${ACTION_PILLS[code] ?? 'pill-neutral'}`;
}

export const KIND_LABELS: Readonly<Record<string, string>> = {
  CLIENT_ANNUAL: 'Client annual',
  UNIT_MONTHLY: 'Unit monthly',
};

export const WARNING_TEXT: Readonly<Record<string, string>> = {
  currency_differs_from_unit: "Budgeted in a currency other than its Business Unit's reporting currency",
};
